import React, { useState, useEffect, useMemo } from 'react'
import './trade-portal-post-modal.styles.scss'
import Modal from '@/components/modal/modal.component'
import { ModalAlert } from '@/components/modal/modal.component'
import { UseCloudGameTransactions } from '@/utils/firebase.utils'
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import ImageContainer from '../image-container/image-container.component'
import { useGameTerms } from '@/utils/gameTerms'

const TRADEABLE_TYPES = ['inventory', 'collectible', 'lottery']

const TradePortalPostModal = ({ show, cancel, gameData, playerData, gameElements }) => {
  const [selectedItem, setSelectedItem] = useState(null)
  const [quantity, setQuantity] = useState(1)
  const [requests, setRequests] = useState([])
  const [addingRequest, setAddingRequest] = useState(false)
  const [reqType, setReqType] = useState('item')
  const [reqItem, setReqItem] = useState(null)
  const [reqQty, setReqQty] = useState(1)
  const [loading, setLoading] = useState(false)
  const [alertMessage, setAlertMessage] = useState(null)
  const terms = useGameTerms()

  useEffect(() => {
    if (!show) {
      setSelectedItem(null)
      setQuantity(1)
      setRequests([])
      setAddingRequest(false)
      setReqType('item')
      setReqItem(null)
      setReqQty(1)
    }
  }, [show])

  // Items protected by completed collections — player must keep at least 1
  const collectionProtectedItemIds = useMemo(() => {
    const protected_ids = new Set()
    if (!playerData?.collections || !gameElements) return protected_ids
    Object.keys(playerData.collections).forEach((collectionId) => {
      const collection = gameElements[collectionId]
      if (collection?.opt_itemList) {
        collection.opt_itemList.forEach((itemId) => protected_ids.add(itemId))
      }
    })
    return protected_ids
  }, [playerData?.collections, gameElements])

  const offerableItems = useMemo(() => {
    if (!playerData?.items || !gameElements) return []
    return Object.entries(playerData.items)
      .filter(([itemId, itemData]) => {
        const el = gameElements[itemId]
        if (!el || !TRADEABLE_TYPES.includes(el.opt_itemType)) return false
        const tradeableQty = itemData.count - (collectionProtectedItemIds.has(itemId) ? 1 : 0)
        return tradeableQty > 0
      })
      .map(([itemId, itemData]) => {
        const el = gameElements[itemId]
        const tradeableQty = itemData.count - (collectionProtectedItemIds.has(itemId) ? 1 : 0)
        return { itemId, el, count: itemData.count, tradeableQty }
      })
      .sort((a, b) => a.el.name.localeCompare(b.el.name))
  }, [playerData?.items, gameElements, collectionProtectedItemIds])

  const requestableItems = useMemo(() => {
    if (!gameElements) return []
    return Object.values(gameElements)
      .filter((el) => {
        if (!TRADEABLE_TYPES.includes(el.opt_itemType)) return false
        const playerOwns = (playerData?.items?.[el.id]?.count ?? 0) > 0
        const isVisible = el.status === 'active' && el.opt_visible && el.opt_preview
        return isVisible || playerOwns
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [gameElements, playerData?.items])

  function handleSelectItem(item) {
    setSelectedItem(item)
    setQuantity(1)
  }

  function handleAddRequest() {
    if (reqType === 'currency' || reqType === 'points') {
      if (reqQty < 1) return
      setRequests((prev) => [...prev, { type: reqType, itemId: null, quantity: reqQty }])
    } else {
      if (!reqItem) return
      setRequests((prev) => [
        ...prev,
        { type: reqItem.opt_itemType, itemId: reqItem.id, quantity: reqQty },
      ])
    }
    setReqItem(null)
    setReqQty(1)
    setAddingRequest(false)
  }

  function getRequestLabel(req) {
    if (req.type === 'currency') return terms.formatCurrency(req.quantity)
    if (req.type === 'points') return `${req.quantity} Points`
    const el = gameElements?.[req.itemId]
    const name = el?.name ?? req.itemId
    return req.quantity > 1 ? `${req.quantity}x ${name}` : name
  }

  async function handleSubmit() {
    if (!selectedItem) return
    setLoading(true)
    const res = await UseCloudGameTransactions(
      'createTradePost', {
        gameId: gameData.gameId,
        playerId: playerData.playerId,
        offering: {
          type: selectedItem.el.opt_itemType,
          itemId: selectedItem.itemId,
          quantity,
        },
        requesting: requests,
      }
    )
    setLoading(false)
    console.log('createTradePost res:', res)
    if (res?.error) {
      setAlertMessage(res.error)
      return
    }
    cancel()
  }

  return (
    <Modal show={show} cancel={cancel} text='Create Trade Post' closeButton={true}>
      <ModalAlert
        show={!!alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage}
      />
      <div className='trade-modal-content'>

        {/* Offering item selection */}
        <div className='form-section'>
          <span className='form-label'>What are you offering?</span>
          <div className='item-grid'>
            {offerableItems.length === 0 ? (
              <span className='no-items-msg'>No tradeable items in your inventory.</span>
            ) : (
              offerableItems.map(({ itemId, el, tradeableQty }) => (
                <button
                  key={itemId}
                  className={`item-chip ${selectedItem?.itemId === itemId ? 'selected' : ''}`}
                  onClick={() => handleSelectItem({ itemId, el, tradeableQty })}
                >
                  {el.icon && <ImageContainer src={el.icon} alt={el.name} />}
                  {el.name}
                  <span className='chip-sub'>(max {tradeableQty})</span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Quantity */}
        {selectedItem && (
          <div className='form-section'>
            <span className='form-label'>How many?</span>
            <div className='field small'>
              <input
                type='number'
                className='uk-input'
                min={1}
                max={selectedItem.tradeableQty}
                value={quantity}
                onChange={(e) =>
                  setQuantity(
                    Math.min(
                      Math.max(1, Number(e.target.value)),
                      selectedItem.tradeableQty
                    )
                  )
                }
              />
            </div>
            <span className='qty-hint'>max: {selectedItem.tradeableQty}</span>
          </div>
        )}

        {/* What do you want in return */}
        <div className='form-section'>
          <span className='form-label'>
            What do you want in return?{' '}
            <span style={{ fontWeight: 400, color: '#999' }}>(optional)</span>
          </span>

          {requests.length > 0 && (
            <div className='request-list'>
              {requests.map((req, i) => (
                <div key={i} className='request-row'>
                  <span className='request-label'>{getRequestLabel(req)}</span>
                  <button
                    className='remove-req-btn'
                    onClick={() =>
                      setRequests((prev) => prev.filter((_, j) => j !== i))
                    }
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {!addingRequest && (
            <button
              className='g-button small-btn'
              onClick={() => setAddingRequest(true)}
            >
              + Add Request
            </button>
          )}

          {addingRequest && (
            <div className='add-request-panel'>
              <div className='form-section'>
                <span className='form-label'>Type:</span>
                <div className='field small'>
                  <select
                    className='uk-select'
                    value={reqType}
                    onChange={(e) => {
                      setReqType(e.target.value)
                      setReqItem(null)
                    }}
                  >
                    <option value='item'>Item</option>
                    <option value='currency'>{terms.currency.plural}</option>
                  </select>
                </div>
              </div>

              {reqType === 'item' && (
                <div className='item-grid'>
                  {requestableItems.map((el) => (
                    <button
                      key={el.id}
                      className={`item-chip ${reqItem?.id === el.id ? 'selected' : ''}`}
                      onClick={() => setReqItem(el)}
                    >
                      {el.icon && <ImageContainer src={el.icon} alt={el.name} />}
                      {el.name}
                    </button>
                  ))}
                </div>
              )}

              <div className='form-section'>
                <span className='form-label'>Qty:</span>
                <div className='field small'>
                  <input
                    type='number'
                    className='uk-input'
                    min={1}
                    value={reqQty}
                    onChange={(e) =>
                      setReqQty(Math.max(1, Number(e.target.value)))
                    }
                  />
                </div>
              </div>

              <div className='panel-actions'>
                <button
                  className='g-button small-btn'
                  onClick={() => setAddingRequest(false)}
                >
                  Cancel
                </button>
                <button
                  className='g-button small-btn primary'
                  disabled={reqType === 'item' && !reqItem}
                  onClick={handleAddRequest}
                >
                  Add
                </button>
              </div>
            </div>
          )}
        </div>

        <div className='modal-footer'>
          <button className='g-button med-btn' onClick={cancel}>
            Cancel
          </button>
          <button
            className='g-button med-btn primary'
            disabled={!selectedItem || loading}
            onClick={handleSubmit}
          >
            {loading ? <ThreeDotsLoader /> : 'Post Trade'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default TradePortalPostModal
